import React, { Fragment, useContext } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useHistory } from "react-router-dom";

import { InputSelectRequiredwithIdAndIcons } from "../../../shared/components/FormElements/InputSelectRequiredwithIdAndIcons";
import { useForm } from "../../../shared/hooks/formHook";
import { SettingsContext } from "../../../shared/context/settingsContext";
import { addPreApprovedFinToProposal } from "../../../sales/api/financingsAPI";
import { addThousandSeparator } from "../../../shared/util/addThousandSeparator";
import { ButtonCyanContained } from "../../../shared/components/MUIThemed/ButtonCyanContained";
import { getFinancingAvatar } from "../../marketplace/util/getFinancingAvatar";
import { AuthContextProps, FinancingData } from "../../../shared/data/types";

import { PopoverFormStyles } from "./PopoverFormStyles";

interface FormAddFinToProposalProps {
  auth: AuthContextProps;
  financing: FinancingData;
  setLoadedData: React.Dispatch<React.SetStateAction<FinancingData[]>>;
  sendRequest: (
    url: string,
    method?: string,
    body?: BodyInit,
    headers?: HeadersInit,
    successMessage?: boolean,
    zeroResultsKey?: string
  ) => Promise<any>;
  isLoading: boolean;
  uniqueFinProps: FinancingData["proposals"];
  continueHandler?: () => void;
  isPopover?: boolean;
}

export const FormAddFinToProposal = (props: FormAddFinToProposalProps) => {
  const {
    auth,
    financing,
    sendRequest,
    setLoadedData,
    isLoading,
    uniqueFinProps,
    isPopover = false,
    continueHandler,
  } = props;
  const classes = PopoverFormStyles();
  const settingsCtx = useContext(SettingsContext);
  const { formState, inputHandler, reportInvalid } = useForm({}, false);
  const history = useHistory();

  const chosenOpt = uniqueFinProps?.find((p) => p?.chosen);
  const hasAddedToProp = uniqueFinProps?.some((p) => p?.addedToProp);

  const initOpts =
    uniqueFinProps?.length > 0
      ? uniqueFinProps.sort((a, b) => {
          // 1. Prioritize bestChoice === true
          if (a.bestChoice && !b.bestChoice) return -1;
          if (!a.bestChoice && b.bestChoice) return 1;

          // 2. Prioritize gracePeriod === 3
          if (a.gracePeriod === 3 && b.gracePeriod !== 3) return -1;
          if (a.gracePeriod !== 3 && b.gracePeriod === 3) return 1;

          // 3. If neither is 3, sort by descending gracePeriod
          if (a.gracePeriod !== 3 && b.gracePeriod !== 3) {
            const graceDiff = b.gracePeriod - a.gracePeriod;
            if (graceDiff !== 0) return graceDiff;
          }

          // 4. Final fallback: sort by descending installmentValue
          return b.installmentValue - a.installmentValue;
        })
      : [];

  const inPropOpts = initOpts?.filter((p) => p.addedToProp) || [];

  const options = uniqueFinProps?.map((p, i) => {
    const avatarPath = getFinancingAvatar(p?.bank);

    return {
      txt: `${
        !chosenOpt && i === 0 ? "ESCOLHA AZUME: " : ""
      }${addThousandSeparator(p?.fee < 0.15 ? p?.fee * 100 : p?.fee)}% - x${
        p?.installments
      } de R$ ${addThousandSeparator(p?.installmentValue)} - ${
        p?.gracePeriod
      } ${p?.gracePeriod === 1 ? "Mês" : "Meses"}`,
      id: p?._id,
      icon: `${avatarPath}`,
      hl:
        !chosenOpt && !hasAddedToProp
          ? i === 0
          : !!chosenOpt
          ? p?.chosen
          : p?.addedToProp,
    };
  });

  const selOpts = [
    formState?.inputs?.option1?.value?.toString(),
    formState?.inputs?.option2?.value?.toString(),
    formState?.inputs?.option3?.value?.toString(),
    formState?.inputs?.option4?.value?.toString(),
  ];

  const hasSelOpt = !!selOpts?.find((o) => !!o);

  const addFinToPropHandler = () => {
    addPreApprovedFinToProposal({
      sendRequest,
      auth,
      fid: financing?._id,
      options: selOpts?.filter((o) => !!o),
      setLoadedFinancings: setLoadedData,
    }).then(() => {
      if (
        !!settingsCtx?.proposalSlug &&
        typeof financing?.proposal?.count === "number" &&
        financing?.proposal?.count > 0 &&
        !continueHandler
      ) {
        history.push(
          `/proposta/final/${settingsCtx.proposalSlug}/${
            financing?.proposal?.count
          }/${financing?.proposal?.frontId?.substring(0, 5)}`
        );
      } else if (!continueHandler) {
        history.push(`/proposta/final/${financing?.proposal?.frontId}`);
      } else {
        continueHandler();
      }
    });
  };

  return (
    <Fragment>
      <form
        style={{ zIndex: 502 }}
        className={!isPopover ? classes.formRoot2 : classes.formRoot}
      >
        <InputSelectRequiredwithIdAndIcons
          id="option1"
          label="Opção 1"
          variant="outlined"
          options={options}
          disabled={!options?.length}
          onInput={inputHandler}
          initialValue={inPropOpts?.[0]?._id || initOpts?.[0]?._id || ""}
          isOptional
        />
        <InputSelectRequiredwithIdAndIcons
          id="option2"
          label="Opção 2"
          variant="outlined"
          options={options}
          disabled={!options?.length}
          onInput={inputHandler}
          initialValue={inPropOpts?.[1]?._id || initOpts?.[1]?._id || ""}
          isOptional
        />
        <InputSelectRequiredwithIdAndIcons
          id="option3"
          label="Opção 3"
          variant="outlined"
          options={options}
          disabled={!options?.length}
          onInput={inputHandler}
          initialValue={inPropOpts?.[2]?._id || initOpts?.[2]?._id || ""}
          isOptional
        />
        <InputSelectRequiredwithIdAndIcons
          id="option4"
          label="Opção 4"
          variant="outlined"
          options={options}
          disabled={!options?.length}
          onInput={inputHandler}
          initialValue={inPropOpts?.[3]?._id || initOpts?.[3]?._id || ""}
          isOptional
        />
        {!isLoading && (
          <ButtonCyanContained
            variant="contained"
            color="primary"
            disabled={!hasSelOpt}
            onClick={addFinToPropHandler}
          >
            ADICIONAR À PROPOSTA
          </ButtonCyanContained>
        )}
        {isLoading && (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              marginTop: "1.5rem",
            }}
          >
            <CircularProgress color="primary" />
          </div>
        )}
      </form>
    </Fragment>
  );
};
